import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { UserDetails } from '../../features/auth/screens/userProps';
import { timeZoneStorage } from '../../features/auth/api/timeZoneStorage';
import secureStorage from '../services/secureStorage';

interface UserState {
  userDetails: UserDetails | null;
  isLoaded: boolean;
}

const initialState: UserState = {
  userDetails: null,
  isLoaded: false,
};

/** secureStorage key used to persist the timezone across page reloads. */
export const TIMEZONE_STORAGE_KEY = 'USER_TIMEZONE_KEY';

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUserInfo(state, action: PayloadAction<UserDetails>) {
      state.userDetails = action.payload;
      state.isLoaded = true;
      // Populate the in-memory timezone store so the axios interceptor
      // can attach the X-TimeZone header to all subsequent API calls.
      const tz = action.payload.keyName;
      if (typeof tz === 'string' && tz.length > 0) {
        timeZoneStorage.set(tz);
        // Persist so the value survives page reloads (rehydrated in AppNavigator bootstrap).
        secureStorage.setItem(TIMEZONE_STORAGE_KEY, tz).catch(() => {
          /* non-blocking — storage failure must not break the app */
        });
      }
    },
    clearUserInfo(state) {
      state.userDetails = null;
      state.isLoaded = false;
      timeZoneStorage.clear();
      secureStorage.removeItem(TIMEZONE_STORAGE_KEY).catch(() => {/* non-blocking */});
    },
  },
});

export const { setUserInfo, clearUserInfo } = userSlice.actions;
export default userSlice.reducer;